/**
 * 「忙完回家」（UF-INTENT F0；C 稿第 2、3、5 节的口径）：
 * - ComeHomeBar：TA 在外面时（有这一趟），私聊输入框上方一条；在家不显示、也不去查（到家由「我回来啦」那条消息说）。按钮「忙完回家」，加上这条安排此刻到哪一步。
 *   状态看实际事件（后端 display_state），不按时钟；预计到家按这一趟当前的行程现算。
 *   请求人看自己那条（可撤回）；别的家人只看中性安排，不显示是谁请的。
 * - ComeHomeConfirm：主人自己那条私聊命中叫回家的说法时（后端写好的 suggested_action），消息下面一条确认条；
 *   点了才建请求（origin=text_confirmed，带这条消息号），不点什么都不变。
 * 能力表 pets.plan_requests 不是 available：只藏按钮和确认条，当前安排和撤回照常显示。
 * 文案：有名字用名字；不说规则、不说系统，不写 AI 字样。
 */
import type { JourneyBrief, MessageSummary, PlanArrangement, PlanRequestResult, PlanRequestView, SafeNodeKind } from "@/shared/contracts";
import { toApiError } from "@/shared/api/errors";
import { formatLocalTime } from "@/shared/time/clock";
import { Button } from "@/shared/ui";
import { useComeHomeCommands, useComeHomeCurrent, usePlanRequestsCapability } from "./useComeHome";
import "./comeHome.css";

function at(iso: string | null, timeZone: string | null): string {
  if (!iso) return "";
  return timeZone ? formatLocalTime(iso, timeZone) : new Date(iso).toISOString().slice(11, 16);
}

function eta(iso: string | null, timeZone: string | null): string {
  return iso ? `，大概 ${at(iso, timeZone)} 到家` : "，几点到家还说不准";
}

const WAITING: Record<SafeNodeKind, string> = {
  min_stay: "再待一小会儿就回家",
  visit_end: "待完这一段就回家",
  shift_end: "打完这班工就回家",
  fixed_service: "照原来的班次回家",
  unsupported: "这一段忙完就回家",
};

/** 请求人看到的那一句。 */
export function requestLine(name: string, request: PlanRequestView, timeZone: string | null): string {
  switch (request.display_state) {
    case "waiting_safe_node":
      return `${name}${WAITING[request.safe_node_kind]}${eta(request.expected_home_at, timeZone)}。`;
    case "returning":
      return `${name}在回家路上了${eta(request.expected_home_at, timeZone)}。`;
    case "home":
      return request.home_at ? `${name} ${at(request.home_at, timeZone)} 到家了。` : `${name}到家了。`;
    case "cannot_now":
      return request.reason_code === "stay_fixed_service" ? `${name}这趟要住在外面、坐定好的班次，暂时回不来。` : `${name}这会儿还回不来。`;
    case "cancelled":
      return "这次的「忙完回家」已经撤回了。";
    case "replaced":
      return "这次的安排已经换成新的了。";
    case "expired":
      return `${name}的行程变了，这次的「忙完回家」不算数了。`;
  }
}

/** 别的家人看到的中性安排：不说是谁请的。 */
export function arrangementLine(name: string, arrangement: PlanArrangement, timeZone: string | null): string {
  return arrangement.state === "returning"
    ? `${name}在回家路上了${eta(arrangement.expected_home_at, timeZone)}。`
    : `${name}已经在安排回家了${eta(arrangement.expected_home_at, timeZone)}。`;
}

/** 点了以后后端没建新请求的几种回答。 */
export function outcomeLine(name: string, result: PlanRequestResult, timeZone: string | null): string | null {
  switch (result.outcome) {
    case "not_out":
      return `${name}就在家呀。`;
    case "already_returning":
      return `${name}已经在回家路上了${eta(result.expected_home_at, timeZone)}。`;
    case "already_arranged":
      return `${name}已经在安排回家了${eta(result.expected_home_at, timeZone)}。`;
    default:
      return null;
  }
}

function errorLine(error: unknown): string {
  const apiError = toApiError(error);
  if (apiError.status === 409 && apiError.code === "VERSION_CONFLICT") return "行程刚变过，看一眼最新的再点。";
  if (apiError.kind === "capability") return "这会儿还叫不了，过一会儿再试。";
  return "没叫成，过一会儿再试。";
}

interface BarProps {
  userId: string | null;
  petId: string;
  name: string;
  journey: JourneyBrief | null;
  timeZone: string | null;
}

export function ComeHomeBar({ userId, petId, name, journey, timeZone }: BarProps) {
  const out = journey !== null;
  const capability = usePlanRequestsCapability(out);
  const current = useComeHomeCurrent(userId, petId, out);
  const { ask, cancel } = useComeHomeCommands(userId, petId);
  const request = current.data?.request ?? null;
  const arrangement = current.data?.arrangement ?? null;
  const open = request?.state === "accepted";
  const canAsk = capability === "available" && journey !== null && !open && arrangement === null;
  const asked = ask.data ? outcomeLine(name, ask.data, timeZone) : null;
  if (!out || (!request && !arrangement && !canAsk)) return null;
  return (
    <section className="ps-come-home" aria-label={`叫${name}回家`}>
      {request ? <p className="ps-come-home__line" role="status">{requestLine(name, request, timeZone)}</p> : null}
      {!request && arrangement ? <p className="ps-come-home__line" role="status">{arrangementLine(name, arrangement, timeZone)}</p> : null}
      {asked ? <p className="ps-come-home__line" role="status">{asked}</p> : null}
      {ask.isError ? <p className="ps-come-home__error" role="alert">{errorLine(ask.error)}</p> : null}
      {cancel.isError ? <p className="ps-come-home__error" role="alert">{errorLine(cancel.error)}</p> : null}
      <div className="ps-come-home__actions">
        {canAsk ? (
          <Button
            variant="primary"
            size="sm"
            loading={ask.isPending}
            onClick={() => ask.mutate({ journeyId: journey.journey_id, itineraryVersion: journey.itinerary_version, origin: "button", sourceMessageId: null })}
          >
            忙完回家
          </Button>
        ) : null}
        {open && request ? (
          <Button size="sm" loading={cancel.isPending} onClick={() => cancel.mutate({ requestId: request.request_id, version: request.version })}>
            撤回
          </Button>
        ) : null}
      </div>
    </section>
  );
}

interface ConfirmProps {
  message: MessageSummary;
  userId: string | null;
  petId: string;
  name: string;
  journey: JourneyBrief | null;
  timeZone: string | null;
}

/** 只挂在主人自己、带 suggested_action=come_home 的那条私聊下面（消息列表里紧跟那条的一个 li）；TA 不在外面、能力没开都不显示。 */
export function ComeHomeConfirm({ message, userId, petId, name, journey, timeZone }: ConfirmProps) {
  const wanted = message.sender === "owner" && message.suggested_action === "come_home" && journey !== null;
  const capability = usePlanRequestsCapability(wanted);
  const { ask } = useComeHomeCommands(userId, petId);
  if (!wanted || capability !== "available") return null;
  const done = ask.data ? (outcomeLine(name, ask.data, timeZone) ?? `好的，${name}忙完这段就回家。`) : null;
  return (
    <li className="ps-come-home-confirm" data-confirm-for={message.message_id}>
      {done ? (
        <p role="status">{done}</p>
      ) : (
        <>
          <p>要{name}忙完这段就回家吗？</p>
          <Button
            size="sm"
            loading={ask.isPending}
            onClick={() => ask.mutate({ journeyId: journey.journey_id, itineraryVersion: journey.itinerary_version, origin: "text_confirmed", sourceMessageId: message.message_id })}
          >
            好，忙完回家
          </Button>
        </>
      )}
      {ask.isError ? <p className="ps-come-home__error" role="alert">{errorLine(ask.error)}</p> : null}
    </li>
  );
}
